<template>
  <div
    class="mapbox-map"
    :style="{ height: typeof height === 'string' ? height : `${height}px` }"
    ref="map"
  ></div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "mapbox-gl/dist/mapbox-gl.css";
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'

export default {
  props: {
    height: {
      type: [Number, String],
      default: 500,
    },
    stylesAvailable: {
      type: Array,
      required: false,
      default: () => {
        return ['hybrid', "street"]; // Not yet in use. See cycleMapStyle method.
      }
    },
    interactions: {
      type: Object,
      required: false,
      default: function() {
        return {
          boxZoom: true,
          dragPan: true,
          keyboard: true,
          scrollZoom: true,
          dragRotate: true,
          touchZoomRotate: true,
          doubleClickZoom: true
        };
      },
    },
    drawControlOptions: {
      type: Object,
      required: false,
    },
    customMarkerOptions: {
      type: Object,
      required: false,
    },
    geolocateControlOptions: {
      type: Object
    }
  },
  data: () => ({
    map: null,
    mapZoom: 19,
    // mapBearing: 0,  // may eventually use this and try to smooth out any drastic changes
    controlDraw: null,
    controlStyle: null,
    controlGeolocate: null,
    controlNavigation: null,
    isFollowingUser: false,
    lastKnownUserLocation: null,
    lastKnownReferencePoint: null, // might be user location or center of map if manually moving map
    mapStyleIdentifiers: {
      'dark': 'dark-v10',
      'light': 'light-v10',
      'streets': 'streets-v11',
      'outdoors': 'outdoors-v11',
      'satellite': 'satellite-v9',
      'hybrid': 'satellite-streets-v11',
    },
    mapStyleDefault: 'streets',
  }),
  watch: {
    interactions() {
      this.setInteractionHandlers();
    },
    height() {
      if (this.map) {
        this.map.resize();
      }
    },
    mapZoom(value) {
      if (this.controlGeolocate) {
        // Update geolocate fitbounds zoom option
        // So map won't zoom in/out when toggling geolocate (active state)
        this.controlGeolocate.options.fitBoundsOptions.zoom = value;
      }
    },
    lastKnownReferencePoint(point) {
      this.emitReferencePoint(point);
    },
    lastKnownUserLocation(point) {
      this.$emit('user-location', point);
    }
  },
  beforeDestroy() {
    // https://docs.mapbox.com/mapbox-gl-js/api/map/#map#remove
    this.map.remove();
  },
  mounted() {
    // Map
    const vm = this;
    vm.$nextTick(function() {
      const mapDiv = vm.$refs.map;
      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN;
      vm.map = new mapboxgl.Map({
        container: mapDiv,
        style: vm.getStyleUri(vm.mapStyleDefault),
        center: [174.762192, -36.84845], // starting position [lng, lat]center: [-73.5804, 45.53483],
        // pitch: 60, // pitch in degrees
        // bearing: vm.mapBearing, // bearing in degrees
        zoom: vm.mapZoom
      });
      // Map events
      vm.map.on("load", () => {
        console.info("[event - load]");
        vm.$emit('map-loaded', vm.map);
        vm.setInteractionHandlers();
        vm.initializeMarkerWatcher();
        // Activate geolocate on load
        if (vm.geolocateControlOptions && vm.geolocateControlOptions.enabled) {
          vm.controlGeolocate.trigger();
        }

        // 3D buildings
        // The 'building' layer in the mapbox-streets vector source contains building-height data from OpenStreetMap.
        // Insert the layer beneath any symbol layer.
        /*
        let layers = vm.map.getStyle().layers;
        let labelLayerId;
        for (var i = 0; i < layers.length; i++) {
          if (layers[i].type === "symbol" && layers[i].layout["text-field"]) {
            labelLayerId = layers[i].id;
            break;
          }
        }
        vm.map.addLayer(
          {
            id: "3d-buildings",
            source: "composite",
            "source-layer": "building",
            filter: ["==", "extrude", "true"],
            type: "fill-extrusion",
            minzoom: 15,
            paint: {
              "fill-extrusion-color": "#aaa",
              // use an 'interpolate' expression to add a smooth transition effect to the
              // buildings as the user zooms in
              "fill-extrusion-height": [
                "interpolate",
                ["linear"],
                ["zoom"],
                15,
                0,
                15.05,
                ["get", "height"]
              ],
              "fill-extrusion-base": [
                "interpolate",
                ["linear"],
                ["zoom"],
                15,
                0,
                15.05,
                ["get", "min_height"]
              ],
              "fill-extrusion-opacity": 0.6
            }
          },
          labelLayerId
        );
        */
      });

      vm.map.on("moveend", (event) => {
        console.info("[event - moveend]");
        // Submit map center as the new reference point if move was NOT due
        // to geolocation event. Otherwise the geolocate event should be
        // used to set the reference point in case not in the center.
        if (!event.geolocateSource) {
          const mapCenter = vm.map.getCenter();
          vm.lastKnownReferencePoint = {
            lat: mapCenter.lat,
            lng: mapCenter.lng,
          };
        }
      });

      vm.map.on("zoomend", () => {
        console.info("[event - zoomend]");
        vm.mapZoom = vm.map.getZoom();
      });

      vm.map.on("styledata", () => {
        vm.$emit("map-styledata");
      });

      // Navigation control
      vm.controlNavigation = new mapboxgl.NavigationControl();
      vm.map.addControl(vm.controlNavigation, "top-right");

      // Draw control
      if (vm.drawControlOptions && vm.drawControlOptions.enabled) {
        const controls = vm.drawControlOptions.controls || {};
        const displayControlsDefault = (Object.keys(controls).length > 0) ? false : true;
        vm.drawControl = new MapboxDraw({
          displayControlsDefault,
          controls,
        });
        vm.map.addControl(vm.drawControl, 'top-left');
      }

      vm.map.on('draw.create', function(event) {
        vm.$emit('draw-create', event);
      });

      vm.map.on('draw.update', function(event) {
        vm.$emit('draw-update', event);
      });

      vm.map.on('draw.delete', function(event) {
        vm.$emit('draw-delete', event);
      });

      vm.map.on('draw.selectionchange', function(event) {
        vm.$emit('draw-selectionchange', event);
      });

      vm.map.on('draw.modechange', function(event) {
        console.log(event);
        vm.$emit('draw-modechange', event);
      });

      // Terrain control
      // https://stackoverflow.com/a/46195061
      class StyleControl {
        onAdd(map){
          this.map = map;
          this.container = document.createElement('button');
          // this.container.textContent = 'Toggle Satellite';
          this.container.innerHTML = '<i id="our-mapbox-terrain-toggle-icon" class="mdi mdi-map"></i>';
          this.container.setAttribute('data-mapbox-style', vm.mapStyleDefault);
          this.container.className = 'our-mapbox-terrain-toggle mapboxgl-ctrl';
          this.container.addEventListener('click', vm.cycleMapStyle);
          return this.container;
        }
        onRemove(){
          this.container.parentNode.removeChild(this.container);
          this.map = undefined;
        }
      }

      vm.controlStyle = new StyleControl();

      vm.map.addControl(vm.controlStyle, 'top-right');

      // Geolocate control
      if (vm.geolocateControlOptions && vm.geolocateControlOptions.enabled) {
        vm.controlGeolocate = new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true
          },
          trackUserLocation: true,
          // showUserLocation: false, // needs false if going back to custom marker
          // showAccuracyCircle: false, // needs false if going back to custom marker
          fitBoundsOptions: {
            linear: true,
            zoom: vm.mapZoom
          },
          ...vm.geolocateControlOptions
        });
        vm.map.addControl(vm.controlGeolocate, "bottom-right");

        // Geolocate events
        vm.controlGeolocate.on('trackuserlocationstart', function () {
          console.info("[event - trackuserlocationstart]");
          vm.$emit('trackuserlocationstart');
          vm.isFollowingUser = true;
          vm.$store.commit("snacktime", { text: "Tracking ON" });
        });

        vm.controlGeolocate.on('trackuserlocationend', function () {
          console.info("[event - trackuserlocationend]");
          vm.$emit('trackuserlocationend');
          vm.isFollowingUser = false;
          vm.$store.commit("snacktime", { text: "Tracking OFF" });
        });


        vm.controlGeolocate.on("geolocate", event => {
          // console.info("[event - geolocate]");
          const point = {
            lat: event.coords.latitude,
            lng: event.coords.longitude,
          };

          vm.lastKnownUserLocation = point;

          // If we're following the user then emit our reference position
          // (if the map is tilted then user location is not map center)
          if (vm.isFollowingUser) {
            vm.lastKnownReferencePoint = point;
            vm.emitReferencePoint(point);
          }

          // -------------------------------------------------------------------------
          // START Trying ways to prevent bearing changes from breaking zoom/tracking
          // -------------------------------------------------------------------------

          // Bearing is outdated by 1 step because geolocate fitBounds is being called first before geolocate event
          // This is Carlo's magic but seems to work in combination with a watcher on the mapZoom data property.
          if (event.coords.heading) {
            event.target.options.fitBoundsOptions.bearing = event.coords.heading;
          }
          // event.target.options.fitBoundsOptions.zoom = this.zoom;

          // this.zoom = this.map.getZoom();
          // this.mapBearing = this.mapBearing + 10;

          // this.map.rotateTo(this.mapBearing);
          // this.map.easeTo({ lat: event.coords.latitude, lon: event.coords.longitude, bearing: this.mapBearing });

          // this.map.rotateTo(90);
          // if (event.heading) {
          //   this.map.setBearing(event.heading);
          // }

          // -------------------------------------------------------------------------
          // END Trying ways to prevent bearing changes from breaking zoom/tracking
          // -------------------------------------------------------------------------

        });
      }
    });
  },
  methods: {
    getMapInstance() {
      // It was getting really complicated to support passing down props
      // to manage data sources, layers and especially event listeners
      // for each layer (plus being able to remove listeners too).
      // So, my shortcut is for any parent components to add a
      // ref to this child component and call this method to
      // then be able to interact with the map directly.
      // (only use this after map has already loaded)
      return this.map;
    },
    emitReferencePoint(point) {
      // point should be { lat, lng }
      this.$emit('reference-point', point);
    },
    initializeMarkerWatcher() {
      // If we are using a custom marker...
      let marker;
      const vm = this;
      // @todo - we should manually remove this watcher inside beforeDestroy method.
      // (I don't think Vue will remove this automatically so these may build up)
      vm.$watch('customMarkerOptions', function (options) {
        if (marker) marker.remove();
        if (options && options.classes && options.coordinates) {
          let el = document.createElement('div');
          options.classes.forEach(className => {
            el.classList.add(className);
          });
          marker = new mapboxgl.Marker(el)
            .setLngLat([options.coordinates.lng, options.coordinates.lat])
            .addTo(vm.map);
        }
      });
    },
    setInteractionHandlers() {
      // https://docs.mapbox.com/mapbox-gl-js/example/toggle-interaction-handlers/
      if (this.map) {
        Object.keys(this.interactions).forEach(handler => {
          const enable = this.interactions[handler] ? true : false;
          if (enable) {
            this.map[handler].enable();
          }
          else this.map[handler].disable();
        })
      }
    },
    getStyleUri(style) {
      const str = (style && this.mapStyleIdentifiers[style])
        ? this.mapStyleIdentifiers[style]
        : this.mapStyleIdentifiers[this.mapStyleDefault];
      return `mapbox://styles/mapbox/${str}`;
    },
    cycleMapStyle(event) {
      event.preventDefault();
      const vm = this;
      const el = event.target;
      let current_style = el.getAttribute('data-mapbox-style');
      // @todo - cycle through vm.styles to find the next style in
      // the array. For now, we are only supporting two styles.
      if (current_style == 'hybrid') {
        vm.map.setStyle(vm.getStyleUri('streets'));
        el.setAttribute('data-mapbox-style', 'streets');
        document.getElementById("our-mapbox-terrain-toggle-icon").className = "mdi mdi-map-outline";
      }
      else {
        vm.map.setStyle(vm.getStyleUri('hybrid'));
        el.setAttribute('data-mapbox-style', 'hybrid');
        document.getElementById("our-mapbox-terrain-toggle-icon").className = "mdi mdi-map";
      }
    }
  }
};
</script>

<style>
  .our-mapbox-terrain-toggle {
    color: #000;
    padding: .3rem;
    border-radius: 4px;
    background-color: #fff;
    text-transform: uppercase;
    box-shadow: 0 0 0 2px rgba(0,0,0,.1);
    width: 29px;
    height: 29px;
  }
  .mapboxgl-popup {
    max-width: 400px;
    color: black;
  }
  .mapboxgl-popup-close-button {
    padding: 0 5px;
  }
</style>
